import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useAuthStore } from '../../store/authStore'
import { getOrganizationUsage } from '../../services/usageService'
import { getUserOrganizations } from '../../services/organizationService'
import { 
  FolderOpen, 
  FileText, 
  HardDrive, 
  Users, 
  TrendingUp,
  Calendar,
  AlertTriangle,
  CheckCircle,
  ArrowUpRight
} from 'lucide-react'

export default function Usage() {
  const { user } = useAuthStore()
  const [usage, setUsage] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    loadUsage()
  }, [user])

  const loadUsage = async () => {
    setLoading(true)
    try {
      if (!user) {
        console.error('No user found')
        return
      }

      // Get user's organization
      const { data: orgs, error: orgError } = await getUserOrganizations(user.id)
      if (orgError || !orgs || orgs.length === 0) {
        console.error('No organization found')
        return
      }

      const organizationId = orgs[0].id

      const { data, error } = await getOrganizationUsage(organizationId)
      
      if (error) {
        console.error('Error loading usage:', error)
        return
      }

      setUsage(data)
    } catch (error) {
      console.error('Error loading usage:', error)
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-10 w-10 sm:h-12 sm:w-12 border-t-2 border-b-2 border-accent"></div>
      </div>
    )
  }

  if (!usage) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-2xl bg-yellow-100 dark:bg-yellow-900/20 flex items-center justify-center mx-auto mb-4">
            <AlertTriangle className="w-6 h-6 sm:w-8 sm:h-8 text-yellow-600 dark:text-yellow-500" />
          </div>
          <h3 className="font-display text-lg sm:text-xl font-semibold mb-2">Unable to Load Usage Data</h3>
          <p className="text-sm sm:text-base text-muted-light dark:text-muted-dark mb-6">Please try again later</p>
          <button
            onClick={loadUsage}
            className="px-4 sm:px-5 py-2 sm:py-2.5 brand-gradient-bg text-white rounded-xl font-semibold shadow-lg shadow-accent/20 hover:opacity-90 transition-opacity text-sm sm:text-base"
          >
            Retry
          </button>
        </div>
      </div>
    )
  }

  const { projects, storage, files, versions, team, recent, subscription } = usage

  // Determine if usage is critical
  const isStorageCritical = storage.percentage >= 90
  const isProjectsCritical = !projects.unlimited && projects.percentage >= 90

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6 sm:space-y-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold mb-1 sm:mb-2">Usage & Limits</h1>
          <p className="text-sm sm:text-base text-muted-light dark:text-muted-dark">
            Monitor your plan usage and resource consumption
          </p>
        </div>
        
        {subscription.plan !== 'pro' && (
          <Link
            to="/subscriptions"
            className="flex items-center justify-center gap-2 px-4 sm:px-5 py-2 sm:py-2.5 brand-gradient-bg text-white rounded-xl font-semibold shadow-lg shadow-accent/20 hover:opacity-90 transition-opacity text-sm sm:text-base w-full sm:w-auto"
          >
            Upgrade Plan
            <ArrowUpRight className="w-4 h-4" />
          </Link>
        )}
      </div>

      {/* Current Plan */}
      <div className="bg-gradient-to-r from-accent to-accent-dark text-white rounded-2xl p-5 sm:p-6 lg:p-8 shadow-lg">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <p className="text-white/70 text-[10px] sm:text-xs uppercase tracking-widest font-bold mb-1 sm:mb-2">Current Plan</p>
            <h2 className="font-display text-2xl sm:text-3xl font-bold capitalize">
              {subscription.plan === 'free' ? 'Free Trial' : `${subscription.plan} Plan`}
            </h2>
            {subscription.expiresAt && (
              <p className="text-white/90 text-xs sm:text-sm mt-2 sm:mt-3">
                Renews on {new Date(subscription.expiresAt).toLocaleDateString('en-US', {
                  month: 'long',
                  day: 'numeric',
                  year: 'numeric'
                })}
              </p>
            )}
          </div>
          <div className="self-end sm:self-auto">
            <div className="flex items-center gap-2 bg-white/10 px-3 sm:px-4 py-1.5 sm:py-2 rounded-xl backdrop-blur-sm text-sm">
              <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5" />
              <span className="font-semibold capitalize text-sm sm:text-base">{subscription.status}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Usage Statistics Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
        {/* Projects */}
        <UsageCard
          icon={FolderOpen}
          title="Projects"
          current={projects.total}
          limit={projects.unlimited ? null : projects.limit}
          percentage={projects.percentage}
          color="blue"
          critical={isProjectsCritical}
        />

        {/* Storage */}
        <UsageCard
          icon={HardDrive}
          title="Storage"
          current={storage.usedFormatted}
          limit={storage.limitFormatted}
          percentage={storage.percentage}
          color="purple"
          critical={isStorageCritical}
        />

        {/* Files */}
        <StatCard
          icon={FileText}
          title="Total Files"
          value={files.total}
          color="green"
        />

        {/* Team Members */}
        <StatCard
          icon={Users}
          title="Team Members"
          value={team.members}
          color="orange"
        />
      </div>

      {/* Recent Activity */}
      <div className="bg-white dark:bg-surface-dark rounded-2xl sm:rounded-3xl shadow-soft border border-gray-100 dark:border-gray-800 p-5 sm:p-6 lg:p-8">
        <div className="flex items-center gap-2 sm:gap-3 mb-5 sm:mb-6">
          <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-accent/10 flex items-center justify-center">
            <TrendingUp className="w-4 h-4 sm:w-5 sm:h-5 text-accent" />
          </div>
          <div>
            <h3 className="font-display text-lg sm:text-xl font-semibold">Recent Activity</h3>
            <p className="text-[10px] sm:text-xs text-muted-light dark:text-muted-dark">Last 30 days</p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          <ActivityStat
            label="New Projects"
            value={recent.projects}
            icon={FolderOpen}
          />
          <ActivityStat
            label="New Versions"
            value={recent.versions}
            icon={Calendar}
          />
          <ActivityStat
            label="Files Uploaded"
            value={recent.files}
            icon={FileText}
          />
        </div>
      </div>

      {/* Additional Stats */}
      <div className="bg-white dark:bg-surface-dark rounded-2xl sm:rounded-3xl shadow-soft border border-gray-100 dark:border-gray-800 p-5 sm:p-6 lg:p-8">
        <h3 className="font-display text-lg sm:text-xl font-semibold mb-5 sm:mb-6">All Time Statistics</h3>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
          <div className="flex items-center justify-between p-4 sm:p-6 bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-900 rounded-2xl border border-gray-200 dark:border-gray-700">
            <span className="text-sm sm:text-base text-muted-light dark:text-muted-dark font-medium">Total Versions</span>
            <span className="text-2xl sm:text-3xl font-black">{versions.total}</span>
          </div>
          <div className="flex items-center justify-between p-4 sm:p-6 bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-900 rounded-2xl border border-gray-200 dark:border-gray-700">
            <span className="text-sm sm:text-base text-muted-light dark:text-muted-dark font-medium">Total Files</span>
            <span className="text-2xl sm:text-3xl font-black">{files.total}</span>
          </div>
        </div>
      </div>

      {/* Warnings */}
      {(isStorageCritical || isProjectsCritical) && (
        <div className="bg-yellow-50 dark:bg-yellow-900/10 border-2 border-yellow-200 dark:border-yellow-800 rounded-2xl p-4 sm:p-5 lg:p-6">
          <div className="flex flex-col sm:flex-row items-start gap-3 sm:gap-4">
            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-yellow-100 dark:bg-yellow-900/20 flex items-center justify-center flex-shrink-0">
              <AlertTriangle className="w-5 h-5 sm:w-6 sm:h-6 text-yellow-600 dark:text-yellow-500" />
            </div>
            <div className="flex-1">
              <h4 className="font-display text-base sm:text-lg font-semibold text-yellow-900 dark:text-yellow-200 mb-2">Usage Warning</h4>
              <p className="text-yellow-700 dark:text-yellow-300 text-xs sm:text-sm mb-3 sm:mb-4">
                You're approaching your plan limits. Consider upgrading to avoid interruptions.
              </p>
              <Link
                to="/subscriptions"
                className="inline-flex items-center gap-2 text-yellow-900 dark:text-yellow-200 font-semibold text-sm hover:gap-3 transition-all"
              >
                View Upgrade Options
                <ArrowUpRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// Usage Card Component with progress bar
function UsageCard({ icon: Icon, title, current, limit, percentage, color, critical }) {
  const colorClasses = {
    blue: {
      bg: 'bg-blue-100 dark:bg-blue-900/20',
      icon: 'text-blue-600 dark:text-blue-400',
      bar: critical ? 'bg-red-500' : 'bg-blue-500',
      text: critical ? 'text-red-600 dark:text-red-400' : 'text-blue-600 dark:text-blue-400'
    },
    purple: {
      bg: 'bg-purple-100 dark:bg-purple-900/20',
      icon: 'text-purple-600 dark:text-purple-400',
      bar: critical ? 'bg-red-500' : 'bg-purple-500',
      text: critical ? 'text-red-600 dark:text-red-400' : 'text-purple-600 dark:text-purple-400'
    },
    green: {
      bg: 'bg-green-100 dark:bg-green-900/20',
      icon: 'text-green-600 dark:text-green-400',
      bar: 'bg-green-500',
      text: 'text-green-600 dark:text-green-400'
    },
    orange: {
      bg: 'bg-orange-100 dark:bg-orange-900/20',
      icon: 'text-orange-600 dark:text-orange-400',
      bar: 'bg-orange-500',
      text: 'text-orange-600 dark:text-orange-400'
    }
  }

  const colors = colorClasses[color]

  return (
    <div className="bg-white dark:bg-surface-dark rounded-2xl shadow-soft border border-gray-100 dark:border-gray-800 p-4 sm:p-5 lg:p-6">
      <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-xl ${colors.bg} flex items-center justify-center mb-3 sm:mb-4`}>
        <Icon className={`w-5 h-5 sm:w-6 sm:h-6 ${colors.icon}`} />
      </div>
      
      <h3 className="text-muted-light dark:text-muted-dark text-[10px] sm:text-xs uppercase tracking-widest font-bold mb-2 sm:mb-3">{title}</h3>
      
      <div className="flex items-baseline gap-1 sm:gap-2 mb-3 sm:mb-4">
        <span className="text-2xl sm:text-3xl font-black">
          {typeof current === 'number' ? current.toLocaleString() : current}
        </span>
        {limit !== null && (
          <span className="text-muted-light dark:text-muted-dark text-xs sm:text-sm">
            / {typeof limit === 'number' ? limit.toLocaleString() : limit}
          </span>
        )}
        {limit === null && (
          <span className="text-green-600 dark:text-green-400 text-xs sm:text-sm font-semibold">Unlimited</span>
        )}
      </div>

      {limit !== null && (
        <>
          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 sm:h-2.5 mb-2">
            <div
              className={`${colors.bar} h-2 sm:h-2.5 rounded-full transition-all duration-500 ease-out`}
              style={{ width: `${Math.min(percentage, 100)}%` }}
            />
          </div>
          <p className={`text-xs sm:text-sm font-medium ${critical ? 'text-red-600 dark:text-red-400' : 'text-muted-light dark:text-muted-dark'}`}>
            {percentage.toFixed(0)}% used
            {critical && ' - Upgrade needed'}
          </p>
        </>
      )}
    </div>
  )
}

// Simple Stat Card
function StatCard({ icon: Icon, title, value, color }) {
  const colorClasses = {
    blue: { bg: 'bg-blue-100 dark:bg-blue-900/20', icon: 'text-blue-600 dark:text-blue-400' },
    purple: { bg: 'bg-purple-100 dark:bg-purple-900/20', icon: 'text-purple-600 dark:text-purple-400' },
    green: { bg: 'bg-green-100 dark:bg-green-900/20', icon: 'text-green-600 dark:text-green-400' },
    orange: { bg: 'bg-orange-100 dark:bg-orange-900/20', icon: 'text-orange-600 dark:text-orange-400' }
  }

  const colors = colorClasses[color]

  return (
    <div className="bg-white dark:bg-surface-dark rounded-2xl shadow-soft border border-gray-100 dark:border-gray-800 p-4 sm:p-5 lg:p-6">
      <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-xl ${colors.bg} flex items-center justify-center mb-3 sm:mb-4`}>
        <Icon className={`w-5 h-5 sm:w-6 sm:h-6 ${colors.icon}`} />
      </div>
      
      <h3 className="text-muted-light dark:text-muted-dark text-[10px] sm:text-xs uppercase tracking-widest font-bold mb-2 sm:mb-3">{title}</h3>
      <p className="text-2xl sm:text-3xl font-black">{value.toLocaleString()}</p>
    </div>
  )
}

// Activity Stat
function ActivityStat({ label, value, icon: Icon }) {
  return (
    <div className="flex items-center gap-3 sm:gap-4 p-4 sm:p-5 bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-900 rounded-2xl border border-gray-200 dark:border-gray-700">
      <div className="w-10 h-10 sm:w-12 sm:h-12 bg-white dark:bg-surface-dark rounded-xl flex items-center justify-center shadow-sm shrink-0">
        <Icon className="w-5 h-5 sm:w-6 sm:h-6 text-accent" />
      </div>
      <div className="min-w-0">
        <p className="text-muted-light dark:text-muted-dark text-xs sm:text-sm font-medium truncate">{label}</p>
        <p className="text-xl sm:text-2xl font-black mt-1">{value}</p>
      </div>
    </div>
  )
}
